import { useEffect, useMemo } from 'react';
import useNRLazyQuery from '../graphql/useNRLazyQuery';
import { LIST_MY_ACRS, LIST_MY_ACRS_CURSOR } from '../graphql/queries/rbac';
import { usePermissionCheck } from '../components/Permissions/useHasPermission';
import { RBAC_PERMISSIONS } from '../pages/NRSettings/NRUsersAndAccess/NRRoles/NRCustomRoles/NRCustomRolePermissions.constants';
import { ACR_RESOURCE_TYPE } from '../pages/NRSettings/NRUsersAndAccess/rbac.constants';
import { buildDisplayTags, normalizeUserGrants } from './userAccess.utils';

const EMPTY_NODES = [];
const RBAC_QUERY_OPTIONS = {
  fetchPolicy: 'cache-and-network'
};

/**
 * Loads the current user's access grants via listMyACRs.
 */
function useCurrentUserAccess() {
  const { allowed: canViewAcrs, loading: isAcrsCapabilityLoading } = usePermissionCheck({
    rbacPermission: RBAC_PERMISSIONS.SETTINGS_ADMINISTRATION_VIEW,
    resourceType: ACR_RESOURCE_TYPE.ORGANIZATION,
    loadingValue: false
  });

  const [acrNodes, acrsLoading, acrsCalled, fetchMyAcrs, , acrsError] = useNRLazyQuery({
    query: LIST_MY_ACRS,
    qlObjKey: 'listMyACRs',
    defaultValue: EMPTY_NODES,
    options: RBAC_QUERY_OPTIONS,
    transformData: data => data?.edges?.map(edge => edge.node) ?? EMPTY_NODES
  });

  useEffect(() => {
    fetchMyAcrs({
      ...RBAC_QUERY_OPTIONS,
      variables: { cursor: LIST_MY_ACRS_CURSOR }
    });
  }, [fetchMyAcrs]);

  const grants = useMemo(() => normalizeUserGrants(acrNodes), [acrNodes]);

  const displayTags = useMemo(() => buildDisplayTags(grants), [grants]);

  const loading = !acrsCalled || acrsLoading;

  return {
    grants,
    displayTags,
    called: acrsCalled,
    loading,
    error: acrsError,
    canViewAcrs,
    isAcrsCapabilityLoading
  };
}

export default useCurrentUserAccess;
